import { z } from 'zod';
import type { ApiError } from '@/shared/api';
import type { SupportStartMismatchError } from '@/shared/auth/support/supportFailure';
import { formatDateTime } from '@/shared/lib/format';

export const supportStartSchema = z.object({
  // Die Authenticator-App zeigt den Code oft in Dreiergruppen; Leerzeichen und Bindestrich zählen nicht.
  code: z
    .string()
    .transform((value) => value.replace(/[\s-]/g, ''))
    .pipe(z.string().regex(/^\d{6}$/, 'Bitte den sechsstelligen Code aus der App eingeben.')),
});

export type SupportStartInput = z.input<typeof supportStartSchema>;
export type SupportStartValues = z.output<typeof supportStartSchema>;

export const emptySupportStart: SupportStartInput = { code: '' };

/** Was der Dialog nach einer Ablehnung zeigt: den Hinweis zur 2FA oder einen Satz mit Grund. */
export type SupportStartProblem =
  | { kind: 'two-factor-missing' }
  | { kind: 'wrong-code'; text: string }
  | { kind: 'message'; text: string };

const messages: Record<string, string> = {
  'SupportSession.BrowserSessionRequired':
    'Bitte melden Sie sich erneut an, um Kunden-Workspaces zu wechseln.',
  'SupportSession.ConfirmationRequired':
    'Bitte bestätigen Sie den Zugriff für die nächsten 48 Stunden.',
  'SupportSession.LockedOut':
    'Ihr Konto ist nach mehreren falschen Codes vorübergehend gesperrt. Bitte später erneut versuchen.',
  'SupportSession.WorkspaceInactive':
    'Dieser Workspace ist gesperrt. Eine Support-Sitzung ist erst nach seiner Freigabe möglich.',
  'SupportSession.WorkspaceNotFound': 'Diesen Workspace gibt es nicht mehr.',
  'SupportSession.NotACustomerWorkspace':
    'Support-Sitzungen sind nur in Kunden-Workspaces möglich.',
  'SupportSession.ActorIneligible':
    'Nur ein aktiver Plattform-Administrator kann eine Support-Sitzung beginnen.',
};

/** Der Server hat begonnen, der Tab hat nicht übernommen: gesagt wird, was aus der Sitzung wurde. */
export function supportStartMismatchProblem({
  workspaceName,
  openUntil,
}: SupportStartMismatchError): SupportStartProblem {
  const outcome = openUntil
    ? `Sie ließ sich nicht wieder beenden und läuft beim Server bis spätestens ${formatDateTime(openUntil)}.`
    : 'Sie wurde gleich wieder beendet.';
  return {
    kind: 'message',
    text: `Die Sitzung in „${workspaceName}“ hat begonnen, gehört aber nicht zur Anmeldung dieses Tabs – vermutlich hat sich in einem anderen Tab ein anderes Konto angemeldet. ${outcome} Bitte laden Sie die Seite neu.`,
  };
}

export function supportStartProblem(error: ApiError): SupportStartProblem {
  if (error.title === 'SupportSession.TwoFactorRequired') return { kind: 'two-factor-missing' };
  if (error.title === 'SupportSession.InvalidTwoFactorCode') {
    return {
      kind: 'wrong-code',
      text: 'Der Code stimmt nicht oder wurde schon verwendet. Bitte den nächsten Code aus der App eingeben.',
    };
  }
  const known = messages[error.title];
  if (known) return { kind: 'message', text: known };
  if (error.status === 0) {
    return {
      kind: 'message',
      text: 'Keine Antwort vom Server. Ob die Sitzung begonnen hat, ist offen. Bitte prüfen Sie die Verbindung und laden Sie die Seite neu, um den aktuellen Workspace abzurufen.',
    };
  }
  if (error.status === 429) {
    return { kind: 'message', text: 'Zu viele Versuche in kurzer Zeit. Bitte eine Minute warten.' };
  }
  const detail = error.detail ? ` ${error.detail}` : '';
  return {
    kind: 'message',
    text: `Die Sitzung konnte nicht beginnen: Fehler ${error.title}.${detail}`,
  };
}
